import { useEffect, useRef, useState } from 'react'
import { LOGO_ALLOWED_MIME_TYPES, LOGO_MAX_FILE_LABEL } from '@tn/shared'
import { readLogoFile } from '../utils/branding'

interface Props {
  value: string | undefined
  onChange: (logoDataUrl: string | undefined) => void
}

export default function LogoUpload({ value, onChange }: Props) {
  const [error, setError] = useState<string | null>(null)
  // Only the most recent selection may update state — a slow read of an earlier file must not overwrite a newer one.
  const latestRequest = useRef(0)

  // A stale error must not outlive the logo it was about (e.g. the parent resets the form on cancel).
  useEffect(() => { setError(null) }, [value])

  const handleFile = async (input: HTMLInputElement) => {
    const file = input.files?.[0]
    // Clear the input so choosing the same file again (after Remove or a rejection) still fires onChange.
    input.value = ''
    if (!file) return
    const requestId = ++latestRequest.current
    setError(null)
    try {
      const dataUrl = await readLogoFile(file)
      if (requestId === latestRequest.current) onChange(dataUrl)
    } catch (err) {
      if (requestId === latestRequest.current) setError(err instanceof Error ? err.message : String(err))
    }
  }

  const remove = () => {
    latestRequest.current++
    setError(null)
    onChange(undefined)
  }

  return (
    <div className="mb-6">
      <label htmlFor="client-logo-input" className="block text-sm font-semibold text-gray-700 mb-1.5">
        Client Logo
        <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
      </label>
      <label
        htmlFor="client-logo-input"
        className="group relative flex h-36 w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-purple-400 hover:bg-purple-50"
      >
        {value ? (
          <>
            <img src={value} alt="Client logo preview" className="max-h-full max-w-full object-contain p-3" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
              <span className="text-xs font-medium text-white">Click to replace</span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <span className="mt-2 text-xs">Click to upload</span>
          </div>
        )}
      </label>
      <input
        id="client-logo-input"
        type="file"
        accept={LOGO_ALLOWED_MIME_TYPES.join(',')}
        onChange={(e) => void handleFile(e.currentTarget)}
        className="hidden"
      />
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-gray-400">
          PNG, JPEG, or SVG, up to {LOGO_MAX_FILE_LABEL}.
        </p>
        {value && (
          <button type="button" onClick={remove} className="text-xs text-red-500 hover:underline">
            Remove
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-500 mt-1">
          {error}{value ? ' Your previous logo was kept.' : ''}
        </p>
      )}
    </div>
  )
}
